import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, RefreshCw, ChevronRight, ChevronLeft, Target, Lock, Sparkles } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../lib/auth';
import redditPixel from '../lib/tracking';
import api from '../lib/api';
import { toast } from 'sonner';

const PAGE_SIZE = 15;

function IntentBadge({ score }) {
  let cls = 'intent-low';
  let ring = 'ring-gray-200';
  if (score >= 70) { cls = 'intent-high'; ring = 'ring-orange-200'; }
  else if (score >= 40) { cls = 'intent-medium'; ring = 'ring-amber-200'; }
  return (
    <span
      className={`${cls} text-xs font-bold px-2.5 py-1 rounded-full inline-block tabular-nums ring-1 ${ring}`}
      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      data-testid="lead-feed-intent-score-badge"
    >
      {score}
    </span>
  );
}

function StatusBadge({ status }) {
  const cls = `status-${status}`;
  return <Badge variant="outline" className={`text-[10px] border ${cls} capitalize font-medium`} data-testid="lead-feed-status-badge">{status}</Badge>;
}

export default function Leads() {
  const { activeBusiness } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({ sort: 'intent_desc', status: '', subreddit: '' });
  const [subreddits, setSubreddits] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [plan, setPlan] = useState('pro');

  const fetchLeads = useCallback(async () => {
    if (!activeBusiness) return;
    setLoading(true);
    try {
      const data = await api.getLeads(activeBusiness._id, { ...filters, page, limit: PAGE_SIZE });
      setLeads(data.leads || []);
      setTotal(data.total || 0);
      setPages(data.pages || 0);
      if (data.plan) setPlan(data.plan);
    } catch {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [activeBusiness, filters, page]);

  const fetchSubreddits = useCallback(async () => {
    if (!activeBusiness) return;
    try {
      const subs = await api.getSubreddits(activeBusiness._id);
      setSubreddits(subs);
    } catch {}
  }, [activeBusiness]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => { fetchSubreddits(); }, [fetchSubreddits]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [filters]);

  const handleRefresh = async () => {
    if (!activeBusiness) return;
    setRefreshing(true);
    try {
      const result = await api.refreshLeads(activeBusiness._id);
      toast.success(`Found ${result.new_leads} new leads!`);
      redditPixel.leadsRefreshed(result.new_leads);
      setPage(1);
      fetchLeads();
    } catch (err) {
      toast.error(err.message || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const filteredLeads = searchTerm
    ? leads.filter(l =>
        l.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.subreddit?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : leads;

  // Page range for pagination buttons
  const pageRange = () => {
    const range = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(pages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) range.push(i);
    return range;
  };

  if (!activeBusiness) {
    return (
      <DashboardLayout title="Leads">
        <div className="flex flex-col items-center justify-center py-20">
          <Target className="w-12 h-12 text-[#9ca3af] mb-4" />
          <h2 className="text-lg font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>No business set up</h2>
          <p className="text-sm text-[#5B667A] mb-4">Add a business to see leads.</p>
          <Button onClick={() => navigate('/onboard')} className="rounded-xl cta-btn" style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}>
            Add Business
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Leads">
      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-wrap items-center gap-3 mb-5"
        data-testid="lead-feed-filters"
      >
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9ca3af]" />
          <Input
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-9 rounded-lg text-sm bg-white shadow-sm"
            data-testid="lead-feed-search"
          />
        </div>
        <Select value={filters.sort} onValueChange={(val) => setFilters(f => ({ ...f, sort: val }))}>
          <SelectTrigger className="w-[155px] h-9 text-sm bg-white shadow-sm"><SelectValue placeholder="Sort by" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="intent_desc">Highest Intent</SelectItem>
            <SelectItem value="intent_asc">Lowest Intent</SelectItem>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="oldest">Oldest First</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.status || 'all'} onValueChange={(val) => setFilters(f => ({ ...f, status: val === 'all' ? '' : val }))}>
          <SelectTrigger className="w-[125px] h-9 text-sm bg-white shadow-sm"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="replied">Replied</SelectItem>
            <SelectItem value="saved">Saved</SelectItem>
            <SelectItem value="ignored">Ignored</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.subreddit || 'all'} onValueChange={(val) => setFilters(f => ({ ...f, subreddit: val === 'all' ? '' : val }))}>
          <SelectTrigger className="w-[155px] h-9 text-sm bg-white shadow-sm"><SelectValue placeholder="Subreddit" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Subreddits</SelectItem>
            {subreddits.map(s => <SelectItem key={s._id} value={s.name}>r/{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          className="h-9 rounded-lg bg-white shadow-sm hover:bg-[#FFF2EA] hover:text-[#FF4500] hover:border-[#FF4500]/20 transition-colors"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-leads-button"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Scanning...' : 'Refresh'}
        </Button>
      </motion.div>

      {/* Summary */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-[#5B667A]">
          {total} leads total — showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
        </span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: PAGE_SIZE }).map((_, i) => (
            <Card key={i} className="p-4 rounded-xl border-[#E6E8EE]">
              <div className="flex items-center gap-4"><Skeleton className="h-4 flex-1" /><Skeleton className="h-4 w-24" /><Skeleton className="h-6 w-12 rounded-full" /><Skeleton className="h-5 w-16 rounded-full" /></div>
            </Card>
          ))}
        </div>
      ) : filteredLeads.length === 0 ? (
        <Card className="p-16 rounded-2xl border-[#E6E8EE] text-center bg-white">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FFF2EA, #FFE8DE)' }}>
            <Target className="w-7 h-7 text-[#FF4500]" />
          </div>
          <h3 className="text-lg font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>No leads found</h3>
          <p className="text-sm text-[#5B667A] mb-6 max-w-sm mx-auto">Try adjusting your filters or refresh to scan for new discussions.</p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" className="rounded-lg text-sm" onClick={() => navigate('/keywords')}>Add Keywords</Button>
            <Button variant="outline" className="rounded-lg text-sm" onClick={() => navigate('/subreddits')}>Add Subreddits</Button>
          </div>
        </Card>
      ) : (
        <div data-testid="lead-feed-table">
          {/* Desktop */}
          <div className="hidden md:block">
            <div className="bg-white rounded-2xl border border-[#E6E8EE] overflow-hidden shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E6E8EE] bg-gradient-to-r from-[#F9FAFB] to-[#F6F7F9]">
                    <th className="text-left text-[11px] font-semibold text-[#5B667A] uppercase tracking-wider px-5 py-3.5">Thread Title</th>
                    <th className="text-left text-[11px] font-semibold text-[#5B667A] uppercase tracking-wider px-4 py-3.5 w-[150px]">Subreddit</th>
                    <th className="text-center text-[11px] font-semibold text-[#5B667A] uppercase tracking-wider px-4 py-3.5 w-[110px]">Intent Score</th>
                    <th className="text-center text-[11px] font-semibold text-[#5B667A] uppercase tracking-wider px-4 py-3.5 w-[100px]">Status</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence mode="wait">
                    {filteredLeads.map((lead, idx) => {
                      const isFirstBlurred = lead.blurred && (idx === 0 || !filteredLeads[idx - 1]?.blurred);
                      return (
                        <React.Fragment key={lead._id}>
                          {/* Upgrade banner — inserted right before first blurred row */}
                          {isFirstBlurred && plan === 'free' && (
                            <tr>
                              <td colSpan={5} className="p-0">
                                <div className="bg-gradient-to-r from-[#FFF8F5] via-[#FFF2EA] to-[#FFF8F5] px-6 py-4 border-y border-[#FF4500]/10">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#FF6A2A] to-[#FF4500] shadow-sm">
                                        <Lock className="w-4 h-4 text-white" />
                                      </div>
                                      <div>
                                        <p className="text-sm font-semibold text-[#0B0F17]">Unlock all {total} leads</p>
                                        <p className="text-xs text-[#5B667A]">Free plan shows 3 leads/day. Upgrade for unlimited access + AI replies.</p>
                                      </div>
                                    </div>
                                    <Button className="h-9 px-5 rounded-xl text-xs font-bold cta-btn shadow-md shadow-[#FF4500]/20" style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}
                                      onClick={() => { redditPixel.upgradeClicked('leads_table'); navigate('/settings'); }} data-testid="upgrade-cta">
                                      <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Upgrade to Pro
                                    </Button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                          <motion.tr
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: Math.min(idx * 0.02, 0.2), duration: 0.25 }}
                            className={`border-b border-[#E6E8EE]/60 last:border-b-0 group ${lead.blurred ? '' : 'lead-row cursor-pointer'}`}
                            onClick={() => !lead.blurred && navigate(`/leads/${lead._id}`)}
                            data-testid="lead-feed-row"
                          >
                            <td className="px-5 py-3.5">
                              <span className={`text-sm font-medium line-clamp-1 ${lead.blurred ? 'text-[#c8ccd4] select-none blur-[3px]' : 'text-[#0B0F17] group-hover:text-[#FF4500] transition-colors'}`}>
                                {lead.blurred ? 'Upgrade to see this lead discussion...' : lead.title}
                              </span>
                            </td>
                            <td className="px-4 py-3.5">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${lead.blurred ? 'bg-[#F3F4F6] text-[#d1d5db] blur-[2px] select-none' : 'text-[#5B667A] bg-[#F6F7F9]'}`}>
                                {lead.blurred ? 'r/hidden' : `r/${lead.subreddit}`}
                              </span>
                            </td>
                            <td className="px-4 py-3.5 text-center">
                              {lead.blurred ? (
                                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#F3F4F6] text-[#d1d5db] blur-[2px] select-none">??</span>
                              ) : (
                                <IntentBadge score={lead.intent_score} />
                              )}
                            </td>
                            <td className="px-4 py-3.5 text-center">
                              {lead.blurred ? (
                                <Lock className="w-3.5 h-3.5 text-[#d1d5db] mx-auto" />
                              ) : (
                                <StatusBadge status={lead.status} />
                              )}
                            </td>
                            <td className="px-2">
                              {!lead.blurred && <ChevronRight className="w-4 h-4 text-[#d1d5db] group-hover:text-[#FF4500] transition-colors" />}
                            </td>
                          </motion.tr>
                        </React.Fragment>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile */}
          <div className="md:hidden space-y-2">
            {filteredLeads.map((lead) => (
              <Card key={lead._id} className={`p-4 rounded-xl border-[#E6E8EE] bg-white ${lead.blurred ? '' : 'cursor-pointer hover:shadow-md transition-shadow'}`}
                onClick={() => !lead.blurred && navigate(`/leads/${lead._id}`)}>
                {lead.blurred ? (
                  <div className="flex items-center gap-3 py-2">
                    <Lock className="w-4 h-4 text-[#d1d5db]" />
                    <span className="text-sm text-[#c8ccd4] blur-[2px] select-none flex-1">Upgrade to see this lead</span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-sm font-medium text-[#0B0F17] line-clamp-2 flex-1">{lead.title}</h3>
                      <IntentBadge score={lead.intent_score} />
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-[#5B667A] bg-[#F6F7F9] px-2 py-0.5 rounded-full">r/{lead.subreddit}</span>
                      <StatusBadge status={lead.status} />
                    </div>
                  </>
                )}
              </Card>
            ))}

            {plan === 'free' && filteredLeads.some(l => l.blurred) && (
              <Card className="p-5 rounded-xl border-[#FF4500]/20 bg-gradient-to-r from-[#FFF8F5] to-[#FFF2EA]">
                <div className="flex items-center gap-3 mb-3">
                  <Sparkles className="w-5 h-5 text-[#FF4500]" />
                  <p className="text-sm font-semibold text-[#0B0F17]">Unlock all {total} leads</p>
                </div>
                <p className="text-xs text-[#5B667A] mb-3">Upgrade to Pro for unlimited leads, AI replies, and more.</p>
                <Button className="w-full h-9 rounded-xl text-xs font-bold cta-btn" style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}
                  onClick={() => navigate('/settings')}>
                  Upgrade to Pro — $49/mo
                </Button>
              </Card>
            )}
          </div>

          {/* ── Pagination ── */}
          {pages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-xs text-[#5B667A]">
                Page {page} of {pages}
              </p>
              <div className="flex items-center gap-1.5">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-lg"
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  data-testid="pagination-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>

                {pageRange().map(p => (
                  <Button
                    key={p}
                    variant={p === page ? 'default' : 'outline'}
                    size="sm"
                    className={`h-8 w-8 rounded-lg text-xs font-semibold p-0 ${
                      p === page
                        ? 'bg-[#FF4500] text-white hover:bg-[#e63e00] shadow-sm'
                        : 'hover:bg-[#FFF2EA] hover:text-[#FF4500] hover:border-[#FF4500]/20'
                    }`}
                    onClick={() => setPage(p)}
                    data-testid={`pagination-page-${p}`}
                  >
                    {p}
                  </Button>
                ))}

                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-lg"
                  disabled={page >= pages}
                  onClick={() => setPage(p => Math.min(pages, p + 1))}
                  data-testid="pagination-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
